import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ViewChild,
} from "@angular/core";

import { Router, RouterModule } from "@angular/router";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { MatDividerModule } from "@angular/material/divider";
import { MatMenuModule } from "@angular/material/menu";
import { LucideAngularModule } from "lucide-angular";
import { NavigationItem } from "../../../../core/models";
import { PUBLIC_NAVIGATION_ITEMS, RBAC_NAVIGATION_ITEMS } from "../../../../core/utils/constants";
import { ThemeService } from "../../../../core/services/theme.service";
import { AuthService } from "../../../../core/services/auth.service";
import { SidebarComponent } from "../sidebar/sidebar.component";
import { MobileHeaderComponent } from "../mobile-header/mobile-header.component";
import { UserModel } from "../../../../core/models/user";

@Component({
  selector: "app-layout",
  imports: [
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    LucideAngularModule,
    SidebarComponent,
    MobileHeaderComponent
],
  templateUrl: "./app-layout.component.html",
  standalone: true,
})
export class AppLayoutComponent {

  @Input() user: UserModel | null = null;
  @Input() currentPage: string | null = "";
  @Output() onNavigate = new EventEmitter<string>();

  @ViewChild('sidebar') sidebar!: SidebarComponent;

  // Mobile menu state
  isMobileMenuOpen = false;
  isMobileView = false;

  publicNavigationItems: NavigationItem[] = PUBLIC_NAVIGATION_ITEMS;
  rbacNavigationItems: NavigationItem[] = [];

  // Theme state
  isDarkTheme = false;

  constructor(
    public router: Router,
    public themeService: ThemeService,
    private authService: AuthService
  ) {
    this.checkViewport();
    // Get initial theme state
    this.isDarkTheme = this.themeService.isDarkTheme$();
    // Filter RBAC navigation items based on user roles
    this.filterRbacNavigationItems();
  }

  /**
   * Filter RBAC navigation items based on user roles from JWT
   * Public items are always visible, RBAC items require specific roles
   * For parent items with children, filter children and only show parent if it has visible children
   * Uses permission-based checking with implication logic
   */
  private filterRbacNavigationItems(): void {
    this.rbacNavigationItems = RBAC_NAVIGATION_ITEMS.map(item => {
      if (item.children) {
        // Filter children based on permissions
        const filteredChildren = item.children.filter(child => 
          this.hasAccessToNavItem(child.id)
        );
        
        // Only include parent if it has at least one visible child
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      // For items without children, check access normally
      return this.hasAccessToNavItem(item.id) ? item : null;
    }).filter(item => item !== null) as NavigationItem[];
  }

  /**
   * Check if user has access to a navigation item
   * Maps navigation item ID to required permissions
   */
  private hasAccessToNavItem(navItemId: string): boolean {
    // For sidebar items, we check write permission for management items
    // and any permission (read/run/write) for regular items
    if (navItemId === 'management/chatbot') {
      return this.authService.hasAnyPermission(['write_chatbot']); // excepción que es singular, no plural
    } else if (navItemId === 'management/user') {
      return this.authService.hasAnyPermission(['write_users']);
    } else if (navItemId === 'params') {
      return this.authService.hasAnyPermission(['write_systemparams']);
    } else if (navItemId === 'chatbot/conversations') {
      return this.authService.hasAnyPermission(['run_chatbotconversations']);
    } else if (navItemId === 'knowledge-base') {
      // TODO: add authorization later
      return true;
    }
    
    // Fallback to resource-based checking for other items
    return this.authService.hasAccessToResource(navItemId);
  }

  @HostListener("window:resize")
  onResize() {
    this.checkViewport();
  }

  private checkViewport() {
    this.isMobileView = window.innerWidth < 768; // md breakpoint
    if (!this.isMobileView) {
      this.isMobileMenuOpen = false;
    }
  }

  navigate(page: string): void {
    this.router.navigate([page]);
    this.onNavigate.emit(page);
    this.isMobileMenuOpen = false; // Close mobile menu after navigation
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.isDarkTheme = this.themeService.isDarkTheme$();
  }

  onContainerClick(): void {
    // Close user menu when clicking outside
    if (this.sidebar && this.sidebar.isUserMenuOpen) {
      this.sidebar.closeUserMenu();
    }
  }
}
